import { useNavigate } from "react-router";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type CreateOrEditResidentGroupFormValues, ResidentGroupForm } from "./ResidentGroupForm";

export function CreateResidentGroupPage(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showFlashToast = useFlashToast();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const mutation = communityGroupMutations.useAddResidentGroup();
  async function onSubmit(request: CreateOrEditResidentGroupFormValues) {
    if (!request.image[0]) {
      showFlashToast({ variant: "alert", message: t("page.create-resident-group.create.notification.failed") });
      throw new Error("Image not provided");
    }
    const image = await uploadFormImage(request.image[0]);
    if (image) {
      mutation.mutate(
        { name: request.name, description: request.description, imageId: image.id },
        {
          onSuccess: (group) => void navigate(routes.interestGroups.details({ slug, id: group.id })),
        },
      );
    }
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={t("page.create-resident-group.create.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            { name: t("page.group-details.breadcrumbs.discovery") },
            { name: t("page.interest-group.list.title"), to: routes.interestGroups.list({ slug }) },
            { name: t("page.create-resident-group.create.title") },
          ]}
        />
      }
    >
      <Form.Container>
        <ResidentGroupForm
          contentLanguageId={undefined}
          defaultValues={{ image: [] }}
          isEditMode={false}
          isSubmitting={mutation.isPending || isUploadingImage}
          onSubmit={onSubmit}
        />
      </Form.Container>
    </DocumentPage>
  );
}
